<template>
    <LoadingPanel
        v-model:collapsed="collapsed"
        :headline="headline"
        :is-pending="isPending"
        :is-error="isError"
        collapsible
    >
        <div
            v-if="data && data?.pages?.[0]?.total !== 0"
            class="analysis-comments-content"
        >
            <div class="analysis-comments-content__table">
                <div ref="tableRef" />
            </div>
            <pbl-button
                v-if="hasNextPage"
                compact
                :disabled="isFetchingNextPage"
                :loading="isFetchingNextPage"
                @click="fetchNextPage()"
            >
                {{ t('common.showMore') }}
            </pbl-button>
        </div>
        <EmptyStatePanelContent v-else />
        <template #context>
            <pbl-button
                v-if="canEditComments"
                slot="context"
                variant="secondary"
                compact
                @click.stop="commentModal?.openComment()"
            >
                <pbl-icon
                    slot="icon"
                    name="plus"
                ></pbl-icon>
                {{ t('common.addComment') }}
            </pbl-button>
        </template>
    </LoadingPanel>
</template>

<script setup lang="ts">
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { useCommentsQuery } from '@/api/queries/comments/useCommentsQuery'
import { computed, ref, toRefs, watch } from 'vue'
import { PebbleTable } from '@pebble/tables'
import { CellComponent, ColumnDefinition, Options } from 'tabulator-tables'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useI18n } from 'vue-i18n'
import { AnalysisType, AnalysisTypes } from '@/configuration/analysis/analysis-types'
import { useConstantsQuery } from '@/api/queries/general/useConstantsQuery'
import { createElement, PencilLine, Trash2 } from 'lucide'
import { CommentDto } from '@/api/generated/HCA'
import { useGlobalConfirmationModal } from '@/composables/useGlobalConfirmationModal'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { chooseLangString } from '@/utils/i18n-utils'
import { StatusTypes } from '@/configuration/status/condition-status'
import { useExportStateStore } from '@/store/exportStateStore'
import { usePermissions } from '@/composables/usePermissions'
import { useCommentsDeleteMutation } from '@/api/mutations/comments/useCommentsDeleteMutation'
import { useConditionStatusCommentModal } from '@/components/modals/comment/useConditionStatusCommentModal'
import { useUserStore } from '@/store/userStore'
import { ComponentType } from '@/configuration/component/component-types'
import { MeasurementTypes } from '@/configuration/measurements/measurement-types'

const collapsed = defineModel<boolean>('collapsed', { default: true })

const props = defineProps<{
    preloadSize?: number,
    componentId: string,
    componentType: ComponentType,
    headline: string,
}>()

const { preloadSize } = toRefs(props)

const { t, locale } = useI18n()
const commentModal = useConditionStatusCommentModal()
const triggerGlobalConfirmationModal = useGlobalConfirmationModal()

const { userPermissions } = usePermissions()
const canEditComments = computed(() => {
    if (props.componentType === ComponentType.Transformer) {
        return userPermissions.value.canEditTransformerComments
    } else if (props.componentType === ComponentType.Generator) {
        return userPermissions.value.canEditGeneratorComments
    }
    return false
})
const userStore = useUserStore()
const userKid = userStore.userData?.kid
const exportStateStore = useExportStateStore()

const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching : isPending, isError: isError } = useCommentsQuery(
    props.componentId,
    props.componentType,
    preloadSize,
)

const { data: constantsData } = useConstantsQuery()

const { mutateAsync: mutateDeleteAsync } = useCommentsDeleteMutation()

const tableRef = ref<HTMLDivElement | null>(null)
const pebbleTable = ref<PebbleTable | null>(null)

const tableRowData = computed(() => data.value?.pages?.flatMap((e) => e?.comments ?? []) ?? [])

const onEditIconClick = (commentDto: CommentDto) => {
    commentModal?.openComment({
        comment: commentDto,
    })
}

const onDeleteIconClick = (commentDto: CommentDto) => {
    const commentId = commentDto.id
    const analysisType = commentDto.analysisType ?? undefined

    if (!commentId) {
        return
    }

    triggerGlobalConfirmationModal({
        headline: t('conditionStatus.content.common.comments.deleteConfirmation.headline'),
        content: t('conditionStatus.content.common.comments.deleteConfirmation.content'),
        confirmButtonText: t('conditionStatus.content.common.comments.deleteConfirmation.headline'),
        onConfirm: async () => {
            await mutateDeleteAsync({
                commentId,
                componentSid: props.componentId,
                componentType: props.componentType,
                analysisType,
            })
        },
    })
}

const getAnalysisTypeLabel = (cell: CellComponent) => {
    const analysisType = [...AnalysisTypes, ...MeasurementTypes].find((e) => e.value === cell.getValue())

    if (!analysisType) {
        return '-'
    }

    const cellDiv = document.createElement('div')
    cellDiv.classList.add('analysis-comments-cell')
    cellDiv.textContent = t(analysisType.labelKey)

    return cellDiv
}

const getMeasurementLabel = (cell: CellComponent) => {
    const rowData: CommentDto = cell.getRow().getData()
    const parameterId = cell.getValue()

    const cellDiv = document.createElement('div')
    cellDiv.classList.add('analysis-comments-cell')

    if (rowData.analysisType === AnalysisType.VisualInspection) {
        const viParameter = constantsData.value?.visualInspectionParameters?.find((e) => e.id === parameterId)
        cellDiv.textContent = viParameter?.name
            ? t(`transformers.conditionStatus.content.visualInspections.table.measurementPoints.${viParameter.name}`)
            : '-'
    } else {
        const parameter = constantsData.value?.parameters?.find((e) => e.id === parameterId)
        if (!parameter) {
            return '-'
        }
        cellDiv.textContent = rowData.unit ? `${rowData.unit} - ${chooseLangString(locale, parameter)}` : chooseLangString(locale, parameter)
    }

    return cellDiv
}

watch([tableRef, tableRowData, locale], ([newTableRef, newTableRowData]) => {
    if (!newTableRef) {
        return
    }

    const columns: ColumnDefinition[] = [
        {
            title: t('conditionStatus.content.comments.table.headers.date'),
            field: 'date',
            formatter: (cell: CellComponent) => {
                return format(new Date(cell.getValue()), DATE_FORMAT)
            },
        },
        {
            title: t('conditionStatus.content.comments.table.headers.reportNumber'),
            field: 'reportNumber',
            minWidth: 150,
        },
        {
            title: t('conditionStatus.content.comments.table.headers.analysisType'),
            field: 'analysisType',
            minWidth: 150,
            // @ts-expect-error ColumnDefinition doesn't properly type the Tooltip return type.
            tooltip: (event: UIEvent, cell: CellComponent) => getAnalysisTypeLabel(cell),
            formatter: (cell: CellComponent) => getAnalysisTypeLabel(cell),
        },
        {
            title: t('conditionStatus.content.comments.table.headers.parameterId'),
            field: 'parameterId',
            // @ts-expect-error ColumnDefinition doesn't properly type the Tooltip return type.
            tooltip: (event: UIEvent, cell: CellComponent) => getMeasurementLabel(cell),
            formatter: (cell: CellComponent) => getMeasurementLabel(cell),
            widthGrow: 2,
        },
        {
            title: t('common.comment'),
            field: 'text',
            formatter: (cell: CellComponent) => {
                const rowData: CommentDto = cell.getRow().getData()

                const cellDiv = document.createElement('div')
                cellDiv.classList.add('analysis-comments-text-cell')

                if (rowData.isOverride) {
                    const title = t('conditionStatus.content.oilAnalyses.overrideComment.title', {
                        from: t(StatusTypes.find((e) => e.value === rowData.previousStatus)?.labelKey ?? ''),
                        to: t(StatusTypes.find((e) => e.value === rowData.newStatus)?.labelKey ?? ''),
                    })

                    const titleSpan = document.createElement('span')
                    titleSpan.classList.add('analysis-comments-text-cell__title')
                    titleSpan.textContent = title
                    cellDiv.appendChild(titleSpan)
                }

                const textSpan = document.createElement('span')
                textSpan.classList.add('analysis-comments-text-cell__text')
                textSpan.textContent = rowData.text ?? '-'
                cellDiv.appendChild(textSpan)

                return cellDiv
            },
            widthGrow: 8,
        },
        {
            title: t('common.kid'),
            field: 'kid',
        },
        {
            title: '',
            field: 'isOverride',
            visible: !!canEditComments.value,
            formatter: (cell: CellComponent) => {
                const isOverride = cell.getValue()
                const rowData: CommentDto = cell.getRow().getData()

                if (isOverride || rowData.kid !== userKid) {
                    return ''
                }

                const cellDiv = document.createElement('div')
                cellDiv.classList.add('analysis-comments-action-cell')

                const editIcon = createElement(PencilLine)
                editIcon.addEventListener('click', () => onEditIconClick(cell.getRow().getData()))
                editIcon.classList.add('analysis-comments-action-cell__icon')
                cellDiv.appendChild(editIcon)

                const deleteIcon = createElement(Trash2)
                deleteIcon.addEventListener('click', () => onDeleteIconClick(cell.getRow().getData()))
                deleteIcon.classList.add('analysis-comments-action-cell__icon')
                cellDiv.appendChild(deleteIcon)

                return cellDiv
            },
        },
    ]

    const tabulatorOptions: Options = {
        layout: 'fitColumns',
        height: '100%',
        pagination: false,
        reactiveData: true,
        data: newTableRowData,
        columnDefaults: {
            headerSort: false,
            vertAlign: 'middle',
        },
        columns,
    }

    pebbleTable.value?.destroy()
    pebbleTable.value = new PebbleTable(newTableRef, tabulatorOptions)
})

watch(data, (newData) => {
    if (!newData) {
        return
    }

    exportStateStore.conditionStatusExportParameters.analysisCommentsLength = newData.pages.flatMap(
        (e) => e?.comments ?? [],
    ).length
})
</script>

<style scoped lang="scss">
.analysis-comments-content {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;

    &__table {
        border: 1px solid $pbl-border-muted;
    }
}
</style>

<style lang="scss">
.analysis-comments-action-cell {
    display: flex;
    gap: $spacing-2xs;

    align-items: center;

    &__icon {
        height: 18px;
        width: 18px;

        color: $pbl-primary;

        &:hover {
            color: color-mix(in srgb, $pbl-primary 80%, black);
        }
    }
}

.analysis-comments-cell {
    overflow: hidden;
    text-overflow: ellipsis;
}

.analysis-comments-text-cell {
    display: flex;
    flex-direction: column;

    gap: $spacing-3xs;
    white-space: pre-wrap;

    &__title {
        font-weight: 500;
    }
}
</style>
